import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { NgIf, formatNumber } from "@angular/common";
import { SharedModule } from "@shared/shared.module";
import { PremiumSummaryComponent } from "@shared/components/premium-summary/premium-summary.component";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ExtraCoverageComponent } from "@shared/components/coverages/extra-coverage/extra-coverage.component";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject, pairwise, startWith, takeUntil } from "rxjs";
import { SatMotorState } from "@store/sat-motor/sat-motor.state";
import { UPDATE_COVERAGE_ALERT, DELETE_COVERAGE, DELETE_EXTRA_COVER_DETAILS, GET_NCD_ENQUIRY, STORE_NCD_INFO, COPY_PREMIUM_CALCULATION, STORE_AV_IND_OVERRIDE } from "@store/sat-motor/sat-motor.action";
import { VehicleNoValidator } from "@shared/classes/validators/vehicle-no.validator";
import { NcdMessageComponent } from "@shared/components/modal/ncd-message/ncd-message.component";
import { NxDialogService, NxModalRef } from "@aposin/ng-aquila/modal";
import { SimpleModalComponent } from "@shared/components/modal/simple-modal/simple-modal.component";
import { FormatDecimalPipe } from "@shared/pipes/format-decimal/format-decimal.pipe";
import { RemoveCommaPipe } from "@shared/pipes/remove-comma/remove-comma.pipe";
import { OkMessageComponent } from "@shared/components/modal/ok-message/ok-message.component";
import { PreviousPolicyErrorPopupComponent } from "@shared/components/modal/previous-policy-error-popup/previous-policy-error-popup.component";
import { SumInsuredFormatterDirective } from "@shared/directives/sum-insured/sum-insured-formatter.directive";

@Component({
  selector: "app-coverage-comprehensive",
  standalone: true,
  imports: [NgIf, SharedModule, SumInsuredFormatterDirective, PremiumSummaryComponent, ExtraCoverageComponent],
  templateUrl: "./coverage-comprehensive.component.html",
  styleUrl: "./coverage-comprehensive.component.scss",
})
export class CoverageComprehensiveComponent implements OnInit {
  @ViewChild(ExtraCoverageComponent) extraCoverageComponentChild: ExtraCoverageComponent;
  @ViewChild('ncdBtn', { read: ElementRef }) ncdBtn: ElementRef;
  @Input() coverageFmGroup!: FormGroup;
  @Input() isComprehensive!: boolean;
  @Input() lov!: any;
  @Input() defaultDriver: any;
  @Input() issuanceInfo: any;
  @Input() partnerInfo: any;

  @Output() onCoverDeleted = new EventEmitter<unknown>();
  @Output() onCoverFormChanged = new EventEmitter<unknown>();
  @Output() alimCustomerEligible = new EventEmitter<unknown>();
  @Output() bettermentEligible = new EventEmitter<unknown>();

  @Select(SatMotorState.currentTypeOfCoverage) currentTypeOfCoverage$: Observable<SatMotorState>;
  @Select(SatMotorState.coverage) coverage$: Observable<SatMotorState>;
  @Select(SatMotorState.coveragePremium) coveragePremium$: Observable<SatMotorState>;
  @Select(SatMotorState.coverageShowAlert) coverageShowAlert$: Observable<SatMotorState>;
  
  private destroy$ = new Subject<void>();

  comprehensiveForm: FormGroup<any>;
  agentDetail: any;
  vehicleNo: any;
  region: any;
  selectedMakeModel: any;
  typeOfCoverage: any;
  sumInsuredRange: boolean;
  avSi: any;
  avSiAvailable: boolean;
  underInsurenceApplies: boolean;
  avIndChecked: boolean;
  isShowExtraCoverage: boolean = false;

  oldValue = {};

  decimalFmt: any;
  numberFmt: any;

  NCD_WO_NOTIFICATION = [
    "ENQ000", "ENQ014", "ENQ016", "ENQ017", "ENQ030", "ENQ044", "ENQ051", "ENQ057", "ENQ060", "ENQ062", 
    "ENQ063", "ENQ067", "ENQ078", "ENQ079", "ENQ080", "ENQ081", "ENQ088", "ENQ089", "ENQ092", "ENQ096"
  ];

  getNcdDisabled: boolean = true;

  ncdMessageModal?: NxModalRef<NcdMessageComponent>;
  warningMessageModal?: NxModalRef<SimpleModalComponent>;
  okMessageModal?: NxModalRef<OkMessageComponent>;

  coverageAlert: any;
  openPreviousPolicyError?: NxModalRef<PreviousPolicyErrorPopupComponent>;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private readonly dialogService: NxDialogService
  ) {
    this.decimalFmt = new FormatDecimalPipe();
    this.numberFmt = new RemoveCommaPipe();
    this.comprehensiveForm = this.fb.group({
      sumInsured: [""],
      avInd: false,
      excessAmount: [{ value: "", disabled: true }],
      basicPremium: [{ value: "", disabled: true }],
      allriderPrem: [{ value: "", disabled: true }],
      annualPremium: [{ value: "", disabled: true }],
      ncdPercentage: [{ value: "", disabled: true }],
      ncdAmount: [{ value: "", disabled: true }],
      prevNcdInsurer: "",
      prevNcdVeh: ["", VehicleNoValidator()],
    });
  }

  ngOnInit() {
    this.agentDetail = this.store.selectSnapshot<any>((state) => state.SatMotorState.motor.step1.agentDetail);
    this.issuanceInfo = this.store.selectSnapshot<any>((state) => state.SatMotorState.motor.step1.issuance.issuance);
    this.vehicleNo = this.issuanceInfo.vehicleNo;
    this.coverageAlert = this.store.selectSnapshot<any>((state) => state.SatMotorState.data.step3?.coverage?.coverageShowDialog);
    
    this.typeOfCoverage = this.store.selectSnapshot<any>((state) => state.SatMotorState.motor.step3?.coverage?.coverageType);
    if (this.typeOfCoverage && this.isComprehensive) {
      this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
      this.comprehensiveForm.patchValue({
        sumInsured: this.decimalFmt.transform(this.typeOfCoverage["sumInsured"]),
        excessAmount: this.decimalFmt.transform(this.typeOfCoverage["excessAmount"]),
        annualPremium:this.decimalFmt.transform( this.typeOfCoverage["annualPremium"]),
        basicPremium: this.decimalFmt.transform(this.typeOfCoverage["basicPremium"]),
        allriderPrem: this.decimalFmt.transform(this.typeOfCoverage["allriderPrem"]),
        ncdPercentage: this.decimalFmt.transform(this.typeOfCoverage["ncdPercentage"]),
        ncdAmount: this.decimalFmt.transform(this.typeOfCoverage["ncdAmount"]),
        prevNcdVeh: this.typeOfCoverage["prevNcdVeh"],
        prevNcdInsurer: this.typeOfCoverage["prevNcdInsurer"],
      });
    }

    this.coverage$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state) {
        const forceDelete = state["forceDelete"];
        if (forceDelete) {
          const coverageType = state["coverageType"];
          if (coverageType && coverageType["coverCode"] === "01") {
            this.deleteCover(null);
          }
        }
      }
    });

    if (this.store.selectSnapshot<any>(state => state.SatMotorState?.isCancelReplaceCoverNote)) {
      this.getNcdDisabled = true;
    }
    else if (this.issuanceInfo.cnoteType === "NWTR" || this.store.selectSnapshot<any>((state) => state.SatMotorState.draftDetails)) {
      this.getNcdDisabled = false;
    } 

    this.currentTypeOfCoverage$.subscribe((state) => {
      if (state) {
        if (state["coverCode"] === "01") {
          this.coverageFmGroup.controls["typeOfCoverage"] = this.comprehensiveForm;
          this.typeOfCoverage = state;
          if (this.typeOfCoverage && this.isComprehensive) {
            let coverListLov = this.store.selectSnapshot<any>((state) => state.SatMotorState.data?.step3?.coverage?.coverListLov);
            for (let coverList of coverListLov) {
              if (coverList["coverCode"] === "01") {
                this.typeOfCoverage = { ...this.typeOfCoverage, coverDescription: coverList["coverDescription"] };
              }
            } 
          }
        }
      }
    });

    this.coverageShowAlert$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state) {
        if (state["coverCode"] === "01") {
          const grossPremiumWarning = state["grossPremiumWarning"];
          const minPremAppliedWarning = state["minPremAppliedWarning"];
          const siWarning = state["siWarning"];
  
          if (state["bettermentEligible"] && !state["bettermentEligibleShowed"]) {
            this.bettermentEligible.emit();
          } 
          else if (state["alimCustomerEligible"] && !state["alimCustomerEligibleShowed"]) {
            this.alimCustomerEligible.emit();
          } 
          else if (grossPremiumWarning && !grossPremiumWarning["valid"]) {
            this.showGrossPremiumWarning(grossPremiumWarning["value"]);
          } 
          else if (minPremAppliedWarning && !state["minPremAppliedWarningNotified"]) {
            this.showMinPremAppliedWarning(minPremAppliedWarning);
          }
          else if (siWarning && !siWarning["valid"]) {
            this.showSIWarning(siWarning["value"]);
          }
        }
      }
    });
    
    this.coveragePremium$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state) {

        if(!this.isComprehensive){
          return;
        }

        this.avSi = null;
        this.sumInsuredRange = this.avSiAvailable = this.underInsurenceApplies = this.avIndChecked = this.isShowExtraCoverage = false;

        const cngeNote = state["cngeNote"];
        if (cngeNote) {
          this.region = cngeNote.region;

          if (cngeNote.bettermentEligible) {
            this.coverageAlert = { ...this.coverageAlert, bettermentEligible: cngeNote.bettermentEligible };
          }

          const riskList = cngeNote["riskList"];
          if (riskList?.length) {

            for (let riskListItem of riskList) {
              this.prevNcdVeh.setValue(riskListItem.prevNcdVeh || riskListItem.vehicleNo);

              if (riskListItem.prevNcdInsurer) {
                this.prevNcdInsurer.setValue(riskListItem.prevNcdInsurer);
              } else if (this.typeOfCoverage && this.typeOfCoverage["prevNcdInsurer"]) {
                this.prevNcdInsurer.setValue(this.typeOfCoverage["prevNcdInsurer"]);
              }
              
              const coverList = riskListItem["coverList"];
              if (coverList?.length) {
                for (let coverListItem of coverList) {
                  if (this.isComprehensive && coverListItem["coverDto"]?.subCovList?.length > 0) {
                    this.isShowExtraCoverage = true;
                  }

                  this.coverageAlert = { ...this.coverageAlert, coverCode: coverListItem["coverCode"] };
                  
                  if (coverListItem["coverCode"] !== "01") return;

                  const sumInsured = coverListItem["sumInsured"];
                  const grossPremium = coverListItem["grossPremium"];
                  
                  this.setRecommendedSumInsuredRange();
                  if (sumInsured) {

                    const siWarning = this.setSIWarning(sumInsured);
                    this.coverageAlert = { ...this.coverageAlert, siWarning: siWarning };

                    const grossPremiumWarning = this.setGrossPremiumWarning(grossPremium);
                    this.coverageAlert = { ...this.coverageAlert, grossPremiumWarning: grossPremiumWarning };

                    const minPremApplied = cngeNote["minPremApplied"];
                    if (minPremApplied) {
                      this.coverageAlert = { ...this.coverageAlert, minPremAppliedWarning: minPremApplied, minPremAppliedWarningNotified: false };
                    }

                    this.setSumInsuredRange(sumInsured)

                    if ([siWarning.valid, grossPremiumWarning.valid].indexOf(true) != -1 && !this.sumInsuredRange)
                      this.setUnderInsurenceApplies(sumInsured);
                    
                    const avInd = coverListItem["avInd"];
                    this.setAgreedValue(avInd);
                    
                    this.typeOfCoverage = {
                      ...this.typeOfCoverage,
                      ...{ grossPremium: grossPremium },
                      ...{ basicPremium: coverListItem["basicPremium"] },
                      ...{ ncdPercentage: coverListItem["ncdPercentage"] },
                      ...{ ncdAmount: coverListItem["ncdAmount"] },
                      ...{ subCoverPremium: coverListItem["subCoverPremium"] },
                    };
                  } else {
                     this.avInd.disable();
                  }
                  
                  if (coverListItem.alimCustomerEligible) {
                    this.coverageAlert = { ...this.coverageAlert, alimCustomerEligible: coverListItem.alimCustomerEligible };
                  }
                }
              }
            }
            
          }

        }

        if (this.coverageAlert) {
          this.store.dispatch(new UPDATE_COVERAGE_ALERT(this.coverageAlert));
        }
      }

      this.store.dispatch(new STORE_AV_IND_OVERRIDE(false));
    });
    
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(new STORE_AV_IND_OVERRIDE(false));
  }

  onChangeForm($event: any) {

    let data = {
      fieldName: $event.target.getAttribute("formControlName"),
      value: $event.target.value,
      oldValue: this.oldValue[$event.target.getAttribute("formControlName")],
    };

    const doChangeForm = {
      sumInsured: () => {

        if (data?.value) {
          this.sumInsured.clearValidators();
          this.sumInsured.markAsUntouched();
        } else {
          this.sumInsured.setValidators(Validators.required);
          this.sumInsured.markAsTouched();
        }
        this.sumInsured.updateValueAndValidity();

        this.avInd.patchValue(false);
        this.avInd.enable();
        if (this.isMotorcycle()) {
          this.avInd.disable();
        }

        this.underInsurenceApplies = false;
        if (data.value) {
          if (data.value > 0) {
            const siMaxValue = 50000000;
            if (data.value > siMaxValue) {
              this.dialogService.open(SimpleModalComponent, {
                showCloseIcon: false, width: "400px", maxWidth: "400px",
                disableClose: true,
                data: { type: "Warning", message: "Maximum sum insured exceeded system limit." },
              })
              .afterClosed().subscribe((result) => {
                this.sumInsured.setValue(0);
                this.onCoverFormChanged.emit();
              });
            } else {
              this.onCoverFormChanged.emit(data);
            }
          }
        }
      },
      prevNcdVeh: () => {
        if (this.store.selectSnapshot<any>(state => state.SatMotorState?.isCancelReplaceCoverNote)) {
          this.getNcdDisabled = true;
        } else if (this.issuanceInfo.cnoteType === "NWTR") {
          this.getNcdDisabled = false;
        } else if (["NWNN", "NWOO", "RN"].includes(this.issuanceInfo.cnoteType)) {
          if (this.prevNcdVeh.valid && this.prevNcdVeh.value && this.prevNcdVeh.value != 'NA') {
            this.getNcdDisabled = false;
          }
        }
      },
    };

    if (["sumInsured"].includes(data.fieldName)) {
      data = { ...data, value: this.numberFmt.transform(data.value), oldValue: this.numberFmt.transform(data.oldValue) }
    }

    doChangeForm[data.fieldName]();
  }

  deleteCover(cover: any) {
    this.store.dispatch(new UPDATE_COVERAGE_ALERT(null)).subscribe((state) => {
      this.coverageAlert = null;
    });

    this.store.dispatch(new COPY_PREMIUM_CALCULATION());
    this.store.dispatch(new DELETE_COVERAGE(cover)).subscribe((state) => {
      this.comprehensiveForm.reset();
      this.onCoverDeleted.emit();
    });

    this.store.dispatch(new DELETE_EXTRA_COVER_DETAILS(cover)).subscribe((state) => {});

    this.typeOfCoverage = this.avSi = null;
    this.sumInsuredRange = this.avSiAvailable = this.underInsurenceApplies = this.avIndChecked = this.isShowExtraCoverage = false;

    this.getNcdDisabled = true;
  }

  isPrivateCars() {
    return ["080100", "080200", "080300"].includes(this.issuanceInfo.productCode);
  }

  isMotorcycle() {
    return ["082100", "082101"].includes(this.issuanceInfo.productCode);
  }

  setRecommendedSumInsuredRange() {
    this.selectedMakeModel = this.store.selectSnapshot<any>((state) => state.SatMotorState.data.step3?.coverage?.selectedMakeModel);
    if (this.selectedMakeModel) {
      this.avSi = {
        E: { avSiMin: this.selectedMakeModel.avEmSiMin, avSiMinDesc: formatNumber(Number(this.selectedMakeModel.avEmSiMin), 'en-US'), 
        	 avSiMax: this.selectedMakeModel.avEmSiMax, avSiMaxDesc: formatNumber(Number(this.selectedMakeModel.avEmSiMax), 'en-US'), 
           },
        W: { avSiMin: this.selectedMakeModel.avWmSiMin, avSiMinDesc: formatNumber(Number(this.selectedMakeModel.avWmSiMin), 'en-US'), 
        	 avSiMax: this.selectedMakeModel.avWmSiMax, avSiMaxDesc: formatNumber(Number(this.selectedMakeModel.avWmSiMax), 'en-US'), 
           },
      };
      if (this.avSi) {
        this.avSiAvailable = 
          this.avSi[this.region].avSiMin > 0  &&
          this.avSi[this.region].avSiMax > 0 &&
          this.selectedMakeModel.inactiveSiInd === "N";
      }
    }
  }  

  setSumInsuredRange(sumInsured) {
    return this.sumInsuredRange = (this.avSiAvailable) && 
      (sumInsured >= this.avSi[this.region].avSiMin && sumInsured <= this.avSi[this.region].avSiMax);
  }  

  setAgreedValue(avInd: boolean) {
    
    this.avIndChecked = false;
    this.avInd.enable();
    const avIndOverride = this.store.selectSnapshot<any>((state) => state.SatMotorState.avIndOverride);

    if (avIndOverride) {
      this.avIndChecked = avInd;
    } else {
      this.avIndChecked = this.avSi[this.region] && this.selectedMakeModel.inactiveSiInd === "N" && this.sumInsuredRange;
    }

    this.avInd.patchValue(this.avIndChecked);

    if (this.isMotorcycle() || !this.avSiAvailable || !this.sumInsuredRange) {
      this.avInd.disable();
    }

    if (this.isBeyondEconomicRepair(this.store.selectSnapshot<any>((state) => state.SatMotorState.motor?.step3?.coverage?.ncdInfo))) {
      this.avIndChecked = false;
      this.avInd.patchValue(false);
      this.avInd.disable();
    } 

  }  

  setUnderInsurenceApplies(sumInsured) {
    return this.underInsurenceApplies = sumInsured < ((90 / 100) * this.avSi[this.region].avSiMin);
  }

  setSIWarning(sumInsured : any) {
    let value : any;
    if (this.isPrivateCars() && sumInsured < 10000) {
      value = "10000";
    }
    if (this.isMotorcycle() && sumInsured < 1000) {
      value = "1000";
    }
    
    if (value) {
      return { valid: false, value: value };
    }
    return { valid: true };
  }
  
  showSIWarning(value : any) {
    if (value) {
      this.warningMessageModal = this.dialogService.open(SimpleModalComponent, {
        showCloseIcon: false, width: "400px", maxWidth: "400px",
        disableClose: true,
        data: { type: "Warning", message: "Sum insured must be at least RM" + formatNumber(Number(value), 'en-US') + "." },
      });
	  
      this.warningMessageModal.afterClosed().subscribe((result) => {
        this.sumInsured.setValue(this.decimalFmt.transform(value));
        this.onCoverFormChanged.emit();
      });
    }
  }
  
  setGrossPremiumWarning(grossPremium : any) {
    let value : any;
    if (this.isPrivateCars() && grossPremium < 50) {
      value = "50";
    }
    if (this.isMotorcycle() && grossPremium < 20) {
      value = "20";
    }
    
    if (value) {
      return { valid: false, value: value };
    }
    return { valid: true };
  }  

  showGrossPremiumWarning(value : any) {
    if (value) {
      this.dialogService.open(SimpleModalComponent, {
        showCloseIcon: false, width: "400px", maxWidth: "400px",
        disableClose: true,
        data: { type: "Warning", message: "Minimum Gross Premium Applied." },
      })
      .afterClosed().subscribe((result) => {
        this.typeOfCoverage = { ...this.typeOfCoverage, ...{ grossPremium: this.decimalFmt.transform(value) } };
        this.onCoverFormChanged.emit(this.typeOfCoverage);
      });
    }
  }

  showMinPremAppliedWarning(value : any) {
    if (value) {
      this.dialogService.open(SimpleModalComponent, {
        showCloseIcon: false, width: "400px", maxWidth: "400px",
        disableClose: true,
        data: { type: "Warning", message: "Minimum Gross Premium Applied." },
      })
      .afterClosed().subscribe((result) => {
        const coverageAlert = this.store.selectSnapshot<any>((state) => state.SatMotorState.data.step3?.coverage?.coverageShowDialog)
        this.store.dispatch(new UPDATE_COVERAGE_ALERT({ ...coverageAlert, minPremAppliedWarningNotified: true }));
      });
    }
  }

  isBeyondEconomicRepair(ncdInfo: any) {
    if (ncdInfo) {
      const cnmtNoteIsmSresps = ncdInfo?.cnmtNoteIsm?.cnmtNoteIsmSresps;
      if (cnmtNoteIsmSresps && cnmtNoteIsmSresps.length && 
          cnmtNoteIsmSresps.filter(cnmtNoteIsmSresps => cnmtNoteIsmSresps.cnmtIsmSrespPk.ismSrespCode === "E007").length > 0) {
            return true;
      }
    }
    
    return false;
  }

  onNcdInsurerChange(event: any) {
    if (event.value === null) {
      this.prevNcdInsurer?.setValue('');
    }
  }

  onClickGetNcd() {
    if (this.getNcdDisabled)
      return;
    
    this.getNcdDisabled = !this.getNcdDisabled;

    this.getNcd();
  }

  getNcd() {
    let vehicleDetailsForm = this.coverageFmGroup.get("vehicleDetails");

    let vehicleClass;
    const selectedMakeModel = this.store.selectSnapshot<any>((state) => state.SatMotorState?.data?.step3.coverage?.selectedMakeModel);
    if (selectedMakeModel?.classCode) {
      vehicleClass = selectedMakeModel?.classCode;
    }

    let coverCode;
    if (this.typeOfCoverage) {
      coverCode = this.typeOfCoverage?.coverCode;
    }
    
    let payload = {
      cnoteType: this.issuanceInfo.cnoteType,
      cnoteDate: this.issuanceInfo.effectiveDate || this.issuanceInfo.cnoteDate,
      productType: this.issuanceInfo.productCode,
      idType: this.partnerInfo.idType1,
      idNo1: this.partnerInfo.idValue1,
      idNo2: this.partnerInfo.idValue2,
      vehicleUse: vehicleDetailsForm.get("vehicleUse").value,
      vehicleNo: vehicleDetailsForm.get("vehicleNo").value,
      vehicleClass: vehicleClass,
      chassisNo: vehicleDetailsForm.get("vehicleChassis").value,
      coverCode: coverCode,
      previousNcdInsurer: this.prevNcdInsurer.value,
      previousNcdVehicle: this.prevNcdVeh.value,
    };

    this.store.dispatch(new GET_NCD_ENQUIRY(payload)).subscribe((state) => {
      let ncdInfo = state.SatMotorState.ncdEnquiryDetails;
      if (ncdInfo) {
        this.store.dispatch(new STORE_NCD_INFO(ncdInfo));
        if (ncdInfo.ismResCode) {
          if (this.NCD_WO_NOTIFICATION.includes(ncdInfo.ismResCode)) {
            if (ncdInfo?.changeNcdToZero) {
              this.dialogService.open(OkMessageComponent, {
                disableClose: true,
                width: "400px", data: { message: ncdInfo.errMsg }
              }).afterClosed().subscribe((result) => {
                this.comprehensiveForm.patchValue({
                  ncdPercentage: this.decimalFmt.transform(0),
                });
                this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
                this.onCoverFormChanged.emit();
              });
            } else {
              this.comprehensiveForm.patchValue({
                ncdPercentage: this.decimalFmt.transform(ncdInfo.currNcdPct),
              });
              this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
              this.onCoverFormChanged.emit();
            }
          }
          else {
            this.dialogService.open(OkMessageComponent, {
              disableClose: true,
              width: "400px", data: { message: ncdInfo.errMsg }
            }).afterClosed().subscribe((result) => {
              if (this.isBeyondEconomicRepair(ncdInfo)) {
                    this.sumInsured.patchValue(0);
                    this.avIndChecked = false;
                    this.avInd.patchValue(false);
                    this.avInd.disable();
              } else {
                this.comprehensiveForm.patchValue({
                  ncdPercentage: this.decimalFmt.transform(ncdInfo.currNcdPct),
                });
                this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
                this.onCoverFormChanged.emit();
              }
            });
          }
        } else {
          this.dialogService.open(OkMessageComponent, {
            disableClose: true,
            width: "400px", data: { message: ncdInfo.errMsg }
          }).afterClosed().subscribe((result) => {
            this.comprehensiveForm.patchValue({
              ncdPercentage: this.decimalFmt.transform(0),
            });
            this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
            this.onCoverFormChanged.emit();
          });
        }
      } else {
        this.dialogService.open(OkMessageComponent, {
          disableClose: true,
          width: "400px", data: { message: "NCD Not Allowed = 0%. Unable to connect to ISM NCD System. Please retry or contact System Administrator." }
        })
        .afterClosed().subscribe((result) => {
          this.comprehensiveForm.patchValue({
            ncdPercentage: this.decimalFmt.transform(0),
          });
          this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
          this.onCoverFormChanged.emit();
        });
      }
    });
  }

  get avInd() {
    return this.comprehensiveForm.controls["avInd"];
  }

  get prevNcdVeh() {
    return this.comprehensiveForm.controls["prevNcdVeh"];
  }
  
  get prevNcdInsurer() {
    return this.comprehensiveForm.controls["prevNcdInsurer"];
  }

  get sumInsured() {
    return this.comprehensiveForm.controls["sumInsured"];
  }

  getNcdBtn() : ElementRef {
    return this.ncdBtn;
  }

  setNcdBtn(disabled: boolean) {
    this.ncdBtn.nativeElement.disabled = disabled;
    this.getNcdDisabled = disabled;
  }

  callComprehensiveComponent(methodName: any) {
    if(methodName === 'callExtraCoverageCalculatePremium'){
      return this.extraCoverageComponentChild.calculatePremium(false);
    }else{
      return this.extraCoverageComponentChild.motorRoadWarriorValidation();
    }
  }

  get screenWidth() {
    return window.innerWidth >= 704;
  }
  
}
